import React, { useState } from "react";
import Modal from "../../../../components/ui/Modal";
import Input from "../../../../components/ui/Input";
import Button from "../../../../components/ui/Button";

const permissions = [
  { id: "view_analytics", label: "View Analytics" },
  { id: "manage_content", label: "Manage Content (Upload, Edit, Delete)" },
  { id: "manage_monetization", label: "Manage Monetization (Products, Memberships)" },
  { id: "manage_community", label: "Manage Community (Comments, Messages)" },
  { id: "manage_team", label: "Manage Team (Invite, Remove)" },
  { id: "view_payouts", label: "View Payouts & Finance" },
  { id: "manage_payouts", label: "Manage Payout Settings" },
];

const InviteMemberModal = ({ isOpen, onClose, onInvite, memberToEdit }) => {
  const [email, setEmail] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // Populate form when memberToEdit changes
  React.useEffect(() => {
    if (memberToEdit) {
      setEmail(memberToEdit.email || "");
      // If the member has permissions array, use it. 
      // Else check if their role implies permissions or start empty.
      // For this prototype, we'll assume new mock members have permissions 
      // or we just default disjoint permissions for legacy ones.
      setSelectedPermissions(memberToEdit.permissions || []);
    } else {
      // Reset for Invite mode
      setEmail("");
      setSelectedPermissions([]);
    }
  }, [memberToEdit, isOpen]);

  const handlePermissionChange = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSubmit = () => {
    if (!email) return;
    onInvite({ 
      email, 
      permissions: selectedPermissions,
      // If editing, we preserve the ID. If inviting, the parent handles ID generation.
      id: memberToEdit?.id 
    });
    onClose();
  };

  const isEditMode = !!memberToEdit;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? "Edit Team Member" : "Invite New Team Member"}>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">
            Email Address
          </label>
          <Input
            type="email"
            placeholder="teammate@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isEditMode} // Disable email editing for security/simplicity
            className={isEditMode ? "bg-slate-100 text-slate-500" : ""}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">
            Permissions
          </label>
          <fieldset className="mt-2 space-y-2">
            {permissions.map((permission) => (
              <div key={permission.id} className="relative flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id={permission.id}
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={selectedPermissions.includes(permission.id)}
                    onChange={() => handlePermissionChange(permission.id)}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor={permission.id}
                    className="font-medium text-slate-700"
                  >
                    {permission.label}
                  </label>
                </div>
              </div>
            ))}
          </fieldset>
        </div>
        <div className="flex justify-end pt-4 space-x-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isEditMode ? "Save Changes" : "Send Invite"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default InviteMemberModal;
